import { useEffect, useState, useCallback } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  BarChart3, TrendingUp, Package, RotateCcw, Star, Truck, Boxes, Target, Sparkles,
  Percent, Users, ShieldCheck, Download, Send, AlertTriangle,
} from "lucide-react";
import api, { downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const PERIODS = [["today", "Today"], ["yesterday", "Yesterday"], ["7d", "7 Days"], ["30d", "30 Days"], ["90d", "90 Days"], ["year", "1 Year"]];
const TABS = [
  ["overview", "Overview", BarChart3], ["sales", "Sales", TrendingUp], ["products", "Products", Package],
  ["funnel", "Funnel", TrendingUp], ["inventory", "Inventory", Boxes], ["promotions", "Promotions", Percent],
  ["reviews", "Reviews", Star], ["returns", "Returns", RotateCcw], ["shipping", "Shipping", Truck],
  ["customers", "Customers", Users], ["health", "Health", ShieldCheck], ["goals", "Goals", Target],
  ["ai", "AI Assistant", Sparkles],
];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

function Card({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export default function SellerAnalytics() {
  const [period, setPeriod] = useState("30d");
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [aiQ, setAiQ] = useState("");
  const [aiAns, setAiAns] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [goal, setGoal] = useState({ sales_target: "", orders_target: "", products_target: "" });

  const ENDPOINT = {
    overview: "seller/overview", sales: "seller/sales", products: "seller/products", funnel: "seller/funnel",
    inventory: "seller/inventory", promotions: "seller/promotions", reviews: "seller/reviews",
    returns: "seller/returns", shipping: "seller/shipping", customers: "seller/customers",
    health: "seller/health", goals: "seller/goals",
  };

  const load = useCallback((t, p) => {
    const ep = ENDPOINT[t];
    if (!ep) return;
    const noPeriod = ["inventory", "reviews", "health", "goals"].includes(t);
    setLoading(true);
    api.get(`/marketplace/analytics/${ep}${noPeriod ? "" : `?period=${p}`}`)
      .then((r) => setData((d) => ({ ...d, [t]: r.data }))).catch(() => setData((d) => ({ ...d, [t]: null })))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(tab, period); }, [tab, period, load]);
  useEffect(() => { if (tab === "ai" || tab === "overview") { api.get("/marketplace/analytics/seller/anomalies").then((r) => setData((d) => ({ ...d, anomalies: r.data }))).catch(() => {}); } }, [tab]);
  useEffect(() => { const g = data.goals?.goal; if (g) setGoal({ sales_target: g.sales_target || "", orders_target: g.orders_target || "", products_target: g.products_target || "" }); }, [data.goals]);
  useEffect(() => { if (data.goals?.celebrate) toast.success("🎯 Monthly goal achieved — great work!"); }, [data.goals?.celebrate]);

  const askAI = async () => {
    if (!aiQ.trim()) return;
    setAiBusy(true); setAiAns("");
    try { const r = await api.post("/marketplace/analytics/seller/ai/ask", { q: aiQ, period }); setAiAns(r.data.answer); }
    catch { toast.error("AI assistant unavailable"); } finally { setAiBusy(false); }
  };
  const saveGoal = async () => {
    try { await api.put("/marketplace/analytics/seller/goals", { sales_target: Number(goal.sales_target || 0), orders_target: Number(goal.orders_target || 0), products_target: Number(goal.products_target || 0) }); toast.success("Goals saved"); load("goals", period); }
    catch { toast.error("Could not save goals"); }
  };
  const dl = (kind) => downloadFile(`/marketplace/analytics/seller/report/${kind}.csv?period=${period}`, `suffle-${kind}-${period}.csv`).catch(() => toast.error("Download failed"));

  const o = data.overview?.overview;
  const sales = data.sales?.sales; const ordersB = data.sales?.orders || data.overview?.overview;
  const prods = data.products; const funnel = data.funnel?.funnel; const invd = data.inventory;
  const promo = data.promotions?.promotions; const rev = data.reviews; const ret = data.returns;
  const ship = data.shipping; const cust = data.customers; const health = data.health; const goals = data.goals;

  return (
    <div data-testid="seller-analytics">
      <PageHeader title="Business Analytics" icon={BarChart3}
        subtitle="Your real business performance — sales, products, inventory, reviews and AI insights. Never fabricated." />

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="period-picker">
        {PERIODS.map(([v, l]) => (
          <button key={v} onClick={() => setPeriod(v)} data-testid={`period-${v}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${period === v ? "brand-gradient-bg text-white" : "border border-slate-200 bg-white text-slate-600"}`}>{l}</button>
        ))}
      </div>

      <div className="mb-5 flex flex-wrap gap-1.5 border-b border-slate-200 pb-2" data-testid="analytics-tabs">
        {TABS.map(([v, l, Icon]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Icon className="h-3.5 w-3.5" /> {l}
          </button>
        ))}
      </div>

      {loading && !data[tab] ? <p className="text-sm text-slate-400">Loading…</p> : null}

      {/* OVERVIEW */}
      {tab === "overview" && o && (
        <div className="space-y-4">
          {(data.anomalies?.anomalies || []).length > 0 && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4" data-testid="overview-nudges">
              <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-blue-700"><Sparkles className="h-4 w-4" /> Smart insights — act on these</p>
              <ul className="space-y-1">
                {data.anomalies.anomalies.slice(0, 4).map((a, i) => <li key={i} className="text-sm text-blue-700">• {a.message}</li>)}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Card testid="ov-sales" label="Sales" value={inr(o.sales)} sub={o.compare?.sales_change_pct != null ? `${o.compare.sales_change_pct > 0 ? "▲" : "▼"} ${Math.abs(o.compare.sales_change_pct)}% vs prev` : null} />
            <Card testid="ov-orders" label="Orders" value={o.orders} />
            <Card testid="ov-units" label="Units Sold" value={o.units} />
            <Card testid="ov-aov" label="Avg Order Value" value={inr(o.aov)} />
            <Card testid="ov-views" label="Product Views" value={o.views} />
            <Card testid="ov-conv" label="Conversion" value={o.conversion != null ? `${o.conversion}%` : "Not enough data"} />
            <Card testid="ov-returns" label="Returns" value={o.returns} />
            <Card testid="ov-refunds" label="Refunds" value={o.refunds} />
            <Card testid="ov-rating" label="Rating" value={o.rating ?? "—"} sub={`${o.reviews} reviews`} />
            <Card testid="ov-pending" label="Pending Settlement" value={inr(o.pending_settlement)} />
            <Card testid="ov-eligible" label="Eligible Settlement" value={inr(o.eligible_settlement)} />
            <Card testid="ov-invval" label="Inventory Value*" value={inr(o.inventory_value)} sub={`${o.low_stock} low · ${o.out_of_stock} out`} />
          </div>
          {(data.trend || []).length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="sales-trend-chart">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><TrendingUp className="h-4 w-4 text-blue-500" /> Sales trend ({data.overview?.range?.label || "this period"})</p>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.trend} margin={{ top: 5, right: 12, bottom: 5, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => (d || "").slice(5)} minTickGap={24} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <Tooltip formatter={(v, n) => [n === "sales" ? inr(v) : v, n === "sales" ? "Sales" : "Orders"]} contentStyle={{ fontSize: 12, borderRadius: 12 }} />
                    <Line type="monotone" dataKey="sales" name="sales" stroke="#2563eb" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400">*Inventory value is an estimate from available stock × price. Financial figures come from finalized records and update periodically (near real-time).</p>
        </div>
      )}

      {/* SALES */}
      {tab === "sales" && sales && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Card testid="sl-gross" label="Gross Sales" value={inr(sales.gross_sales)} />
            <Card testid="sl-disc" label="Discounts" value={inr(sales.discounts)} />
            <Card testid="sl-refunds" label="Refunds" value={inr(sales.refunds)} />
            <Card testid="sl-net" label="Net Sales" value={inr(sales.net_sales)} />
            <Card testid="sl-tax" label="Tax" value={inr(sales.tax)} />
            <Card testid="sl-comm" label="Commission" value={inr(sales.commission)} />
            <Card testid="sl-payable" label="Seller Payable" value={inr(sales.seller_payable)} />
            <Card testid="sl-settled" label="Settled" value={inr(sales.settled)} />
          </div>
          <button onClick={() => dl("sales")} data-testid="dl-sales" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600"><Download className="h-4 w-4" /> Sales report (CSV)</button>
          <button onClick={() => downloadFile(`/marketplace/seller/statement.pdf?period=${period}`, `suffle-statement-${period}.pdf`).catch(() => toast.error("Download failed"))} data-testid="dl-statement-pdf" className="ml-2 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Download className="h-4 w-4" /> Sales &amp; settlement statement (PDF)</button>
        </div>
      )}

      {/* PRODUCTS */}
      {tab === "products" && prods && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => dl("products")} data-testid="dl-products" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600"><Download className="h-4 w-4" /> Products CSV</button>
          </div>
          <Table testid="products-table" head={["Product", "Views", "Add-Cart", "Orders", "Units", "Revenue", "Conv%", "Returns", "Stock"]}
            rows={(prods.products || []).map((p) => [p.name, p.views, p.add_to_cart, p.orders, p.units, inr(p.revenue), pct(p.conversion), p.returns, p.stock_status])} empty="No product activity in this period." />
          <div className="grid gap-3 sm:grid-cols-2">
            <MiniList testid="top-products" title="Top Products" items={prods.top} render={(x) => `${x.name} — ${inr(x.revenue)}`} />
            <MiniList testid="low-conversion" title="Low Conversion (high views, few sales)" items={prods.low_conversion} render={(x) => `${x.name} — ${x.views} views, ${pct(x.conversion)}`} />
            <MiniList testid="high-return" title="High Return" items={prods.high_return} render={(x) => `${x.name} — ${x.returns} returns`} />
            <MiniList testid="fast-moving" title="Fast Moving" items={prods.fast_moving} render={(x) => `${x.name} — ${x.units} units`} />
          </div>
        </div>
      )}

      {/* FUNNEL */}
      {tab === "funnel" && funnel && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="funnel-panel">
          {!funnel.enough_data ? <p className="text-sm text-slate-500" data-testid="funnel-empty">{funnel.message}</p> : (
            <div className="space-y-3">
              {["impression", "view", "cart", "purchase"].map((k) => (
                <div key={k} className="flex items-center gap-3">
                  <span className="w-28 text-sm capitalize text-slate-600">{k}</span>
                  <div className="h-6 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full brand-gradient-bg" style={{ width: `${Math.min(100, (funnel.stages[k] / (funnel.stages.impression || funnel.stages.view || 1)) * 100)}%` }} />
                  </div>
                  <span className="w-14 text-right text-sm font-semibold">{funnel.stages[k]}</span>
                </div>
              ))}
              <p className="pt-2 text-xs text-slate-500">View→Cart {funnel.rates.view_to_cart}% · Cart→Purchase {funnel.rates.cart_to_purchase}% · View→Purchase {funnel.rates.view_to_purchase}%</p>
            </div>
          )}
        </div>
      )}

      {/* INVENTORY */}
      {tab === "inventory" && invd && (
        <div className="space-y-4">
          {(invd.restock_alerts || []).length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="restock-alerts">
              <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-amber-700"><AlertTriangle className="h-4 w-4" /> Restock estimates</p>
              {invd.restock_alerts.map((a) => <p key={a.product_id} className="text-sm text-amber-700">• {a.message}</p>)}
            </div>
          )}
          <button onClick={() => dl("inventory")} data-testid="dl-inventory" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600"><Download className="h-4 w-4" /> Inventory CSV</button>
          <Table testid="inventory-table" head={["Product", "Stock", "Reserved", "Available", "Status", "Sold 30d", "Days cover"]}
            rows={(invd.products || []).map((p) => [p.name, p.stock, p.reserved, p.available, p.status, p.sold_30d, p.days_of_cover ?? "—"])} empty="No products." />
        </div>
      )}

      {/* PROMOTIONS */}
      {tab === "promotions" && (
        <Table testid="promotions-table" head={["Campaign", "Type", "Code", "Status", "Used", "Orders", "Revenue"]}
          rows={(promo || []).map((p) => [p.name, p.type, p.code || "—", p.status, p.used, p.orders, inr(p.revenue)])}
          empty="No promotions found for your store in this period." />
      )}

      {/* REVIEWS */}
      {tab === "reviews" && rev && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Card testid="rv-avg" label="Average Rating" value={rev.average_rating ?? "—"} />
          <Card testid="rv-total" label="Total Reviews" value={rev.total_reviews} />
          <Card testid="rv-verified" label="Verified" value={rev.verified_reviews} />
          <Card testid="rv-helpful" label="Helpful Votes" value={rev.helpful_votes} />
          <Card testid="rv-reported" label="Reported" value={rev.reported} />
          <Card testid="rv-response" label="Response Rate" value={`${rev.response_rate}%`} />
        </div>
      )}

      {/* RETURNS */}
      {tab === "returns" && ret && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card testid="rt-return" label="Return Rate" value={`${ret.return_rate}%`} sub={`${ret.returns} returns`} />
            <Card testid="rt-refund" label="Refund Rate" value={`${ret.refund_rate}%`} sub={`${ret.refunds} refunds`} />
            <Card testid="rt-cancel" label="Cancellation Rate" value={`${ret.cancellation_rate}%`} sub={`${ret.cancellations} cancelled`} />
            <Card testid="rt-total" label="Total Orders" value={ret.total_orders} />
          </div>
          <Table testid="return-reasons" head={["Reason", "Count"]} rows={(ret.reasons || []).map((x) => [x.reason, x.count])} empty="No recorded return reasons." />
        </div>
      )}

      {/* SHIPPING */}
      {tab === "shipping" && ship && (
        <div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card testid="sh-shipped" label="Shipped" value={ship.shipped} />
            <Card testid="sh-delivered" label="Delivered" value={ship.delivered} />
            <Card testid="sh-delayed" label="Delayed (>3d)" value={ship.delayed} />
            <Card testid="sh-avg" label="Avg Delivery" value={ship.avg_delivery_days != null ? `${ship.avg_delivery_days}d` : "—"} />
          </div>
          <p className="mt-2 text-xs text-slate-400">{ship.data_note}</p>
        </div>
      )}

      {/* CUSTOMERS */}
      {tab === "customers" && cust && (
        <div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Card testid="cu-total" label="Customers" value={cust.total_customers} />
            <Card testid="cu-new" label="New" value={cust.new_customers} />
            <Card testid="cu-repeat" label="Repeat" value={cust.repeat_customers} />
            <Card testid="cu-rate" label="Repeat Rate" value={cust.repeat_rate != null ? `${cust.repeat_rate}%` : "Not enough data"} />
            <Card testid="cu-aov" label="Avg Order Value" value={inr(cust.avg_order_value)} />
          </div>
          <p className="mt-2 text-xs text-slate-400">{cust.note}</p>
        </div>
      )}

      {/* HEALTH */}
      {tab === "health" && health && (
        <div className="space-y-4" data-testid="health-panel">
          <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-center">
              <p className="text-5xl font-extrabold text-slate-900" data-testid="health-score">{health.score}</p>
              <p className={`text-sm font-semibold ${health.band === "Excellent" ? "text-emerald-600" : health.band === "Good" ? "text-blue-600" : "text-amber-600"}`}>{health.band}</p>
            </div>
            <div className="flex-1 space-y-2">
              {(health.factors || []).map((f) => (
                <div key={f.factor} data-testid={`health-factor-${f.factor.replace(/\s+/g, "-")}`}>
                  <div className="flex justify-between text-xs text-slate-600"><span>{f.factor}</span><span>{f.score}/{f.max}</span></div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full brand-gradient-bg" style={{ width: `${f.value_pct}%` }} /></div>
                  <p className="mt-0.5 text-[11px] text-slate-400">{f.tip}</p>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400">{health.note}</p>
        </div>
      )}

      {/* GOALS */}
      {tab === "goals" && goals && (
        <div className="max-w-lg space-y-4" data-testid="goals-panel">
          {goals.celebrate && (
            <div className="flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="goal-celebration">
              <Sparkles className="h-6 w-6 shrink-0 text-amber-500" />
              <div>
                <p className="font-bold text-amber-700">Goal achieved — congratulations!</p>
                <p className="text-sm text-amber-600">You've crossed your monthly {Object.entries(goals.achieved).filter(([, v]) => v).map(([k]) => k).join(" & ")} target this month. Keep it up!</p>
              </div>
            </div>
          )}
          {["sales", "orders", "products"].map((k) => {
            const cur = goals.current[k]; const prog = goals.progress[k];
            return (
              <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold capitalize text-slate-700">{k} goal (this month)</span>
                  <input type="number" value={goal[`${k}_target`]} onChange={(e) => setGoal({ ...goal, [`${k}_target`]: e.target.value })}
                    data-testid={`goal-${k}`} placeholder="Target" className="w-28 rounded-lg border border-slate-200 px-2 py-1 text-sm" />
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full brand-gradient-bg" style={{ width: `${Math.min(100, prog || 0)}%` }} /></div>
                <p className="mt-1 text-xs text-slate-500">Current: {k === "sales" ? inr(cur) : cur} {prog != null ? `· ${prog}%` : ""}</p>
              </div>
            );
          })}
          <button onClick={saveGoal} data-testid="save-goals" className="rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white">Save goals</button>
        </div>
      )}

      {/* AI ASSISTANT */}
      {tab === "ai" && (
        <div className="space-y-4" data-testid="ai-panel">
          {(data.anomalies?.anomalies || []).length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="anomaly-panel">
              <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-amber-700"><AlertTriangle className="h-4 w-4" /> Things to review</p>
              {data.anomalies.anomalies.map((a, i) => <p key={i} className="text-sm text-amber-700">• {a.message}</p>)}
            </div>
          )}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="mb-2 text-sm font-semibold text-slate-700">Ask about your business</p>
            <div className="flex flex-wrap gap-2">
              {["How did my business perform this month?", "Which products sold the most?", "Which products get views but no sales?", "How much commission was deducted?"].map((s) => (
                <button key={s} onClick={() => setAiQ(s)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:border-blue-300">{s}</button>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input value={aiQ} onChange={(e) => setAiQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAI()} data-testid="ai-input"
                placeholder="e.g. Why did sales fall last week?" className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-blue-400" />
              <button onClick={askAI} disabled={aiBusy} data-testid="ai-ask" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Send className="h-4 w-4" /> {aiBusy ? "Thinking…" : "Ask"}</button>
            </div>
            {aiAns && <div className="mt-3 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-slate-700" data-testid="ai-answer">{aiAns}</div>}
            <p className="mt-2 text-xs text-slate-400">Answers use only your real authorized data — Suffle AI never invents figures.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Table({ head, rows, empty, testid }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid={testid}>
      {rows.length === 0 ? <p className="p-8 text-center text-sm text-slate-500">{empty}</p> : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs uppercase text-slate-400">{head.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-slate-50">{r.map((c, j) => <td key={j} className="px-3 py-2.5 text-slate-700">{c}</td>)}</tr>)}</tbody>
        </table>
      )}
    </div>
  );
}

function MiniList({ title, items, render, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="mb-2 text-sm font-semibold text-slate-700">{title}</p>
      {(!items || items.length === 0) ? <p className="text-xs text-slate-400">Not enough data yet.</p> : (
        <ul className="space-y-1 text-sm text-slate-600">{items.map((x) => <li key={x.product_id}>• {render(x)}</li>)}</ul>
      )}
    </div>
  );
}
